import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import DataGrid from '../DataGrid/DataGrid.jsx';
import { Spinner } from '../index.jsx';
import { IconButton } from '../controls/Controls.jsx';
import { useDataGridTextFilter } from '../DataGrid/filters/useDataGridTextFilter.jsx';
import {
  dataGridNumberSorter,
  dataGridTextSorter,
} from '../DataGrid/sorters/DataGridSorters.jsx';
import EduCourseDatesNestedTable from './EduCourseDatesNestedTable.jsx';
import useIsUserAdmin from '../../../hooks/useIsUserAdmin.jsx';
import usePermission from '../../../hooks/usePermission.jsx';
import {
  formattedMorphUserName,
  isUserAndItsMorphIdentical,
} from '../../../utils/helpers/CommonHelper.jsx';
import { EDU_COURSE_KEYWORDS_DELIMITER } from '../../../utils/constants/common_constants.js';
import { FRI_EDU_DATE_STATUS } from '../../../utils/constants/enums.js';
import { ROLE_FRI_EDU_ADMIN } from '../../../utils/constants/roles.js';
import { FileSearchOutlined, PlusOutlined } from '@ant-design/icons';

// Helper renderers / formatters
const getDatesCount = (dates) => dates?.length || 0;
const getPrice = (price) =>
  new Intl.NumberFormat('sk-SK', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
  }).format(price);
const buildNameString = (eduCourse) =>
  `${eduCourse.name} ${(eduCourse.keywords || '').split(EDU_COURSE_KEYWORDS_DELIMITER).join(' ')}`;

// Active date states for delete logic
const ACTIVE_STATES = [
  FRI_EDU_DATE_STATUS.WAITING.STATE,
  FRI_EDU_DATE_STATUS.FULLY_BOOKED.STATE,
  FRI_EDU_DATE_STATUS.REGISTRATION_OPEN.STATE,
  FRI_EDU_DATE_STATUS.REGISTRATION_CLOSED.STATE,
  FRI_EDU_DATE_STATUS.ONGOING.STATE,
].map((s) => String(s).toLowerCase());

const canDeleteCourse = (row) => {
  const terms = Array.isArray(row?.__terms) ? row.__terms : [];
  if (terms.length === 0) return true; // no terms -> can delete
  const hasActive = terms.some((d) =>
    ACTIVE_STATES.includes(String(d?.status || '').toLowerCase()),
  );
  return !hasActive;
};

// Settings (actions) component for each course row
export function EduCoursesTableSettings({
  user,
  eduCourse,
  onShowDatesClick,
  onDetailClick,
}) {
  const { t } = useTranslation();

  return (
    <>
      {onDetailClick ? (
        <IconButton
          icon={<FileSearchOutlined />}
          tooltip={t('Zobraziť detail kurzu')}
          onClick={(e) => {
            e.stopPropagation();
            onDetailClick(eduCourse);
          }}
        />
      ) : null}

      {onShowDatesClick &&
      isUserAndItsMorphIdentical(user, eduCourse.lecturerable) ? (
        <IconButton
          icon={<PlusOutlined />}
          tooltip={t('Pridať termín')}
          onClick={(e) => {
            e.stopPropagation();
            onShowDatesClick(eduCourse);
          }}
        />
      ) : null}
    </>
  );
}

const EduCoursesTable = ({
  courses,
  onDelete,
  onEditClick,
  onRowClick,
  onDetailClick,
  onShowDatesClick,
  isPublic = false,
  expandedRowKeys,
  onExpandedRowsChange,
  onRefetchCourses,
}) => {
  const { t } = useTranslation();
  const user = useSelector((s) => s.auth.user);
  const isAdmin = useIsUserAdmin();
  const canUpdate = usePermission('fri-edu-course-dates.update');

  const topRows = useMemo(() => {
    const list = Array.isArray(courses)
      ? courses
      : Array.isArray(courses?.data)
        ? courses.data
        : [];
    return list.map((c) => {
      const { children, ...rest } = c || {};
      return { ...rest, __terms: Array.isArray(children) ? children : [] };
    });
  }, [courses]);

  // Filters
  const nameFilterProps = useDataGridTextFilter(undefined, buildNameString);
  const lecturableFilterProps = useDataGridTextFilter(
    'lecturerable',
    formattedMorphUserName,
  );
  const priceFilterProps = useDataGridTextFilter('price', getPrice);
  const hoursFilterProps = useDataGridTextFilter('number_hours');
  const datesFilterProps = useDataGridTextFilter('__terms', getDatesCount);

  const columns = useMemo(
    () => [
      {
        title: t('Názov kurzu'),
        dataIndex: 'name',
        ellipsis: true,
        sorter: dataGridTextSorter('name'),
        ...nameFilterProps,
      },
      {
        title: t('Hlavný lektor'),
        dataIndex: ['lecturerable'],
        ellipsis: true,
        render: formattedMorphUserName,
        sorter: dataGridTextSorter('lecturerable', formattedMorphUserName),
        ...lecturableFilterProps,
      },
      {
        title: t('Cena'),
        dataIndex: 'price',
        render: getPrice,
        width: 100,
        align: 'right',
        sorter: dataGridNumberSorter('price'),
        ...priceFilterProps,
      },
      {
        title: t('Hodiny'),
        dataIndex: 'number_hours',
        width: 120,
        align: 'right',
        sorter: dataGridNumberSorter('number_hours', (v) => parseFloat(v)),
        ...hoursFilterProps,
      },
      {
        title: t('Termíny'),
        dataIndex: ['__terms'],
        render: getDatesCount,
        width: 120,
        align: 'right',
        sorter: dataGridNumberSorter('__terms', getDatesCount),
        ...datesFilterProps,
      },
    ],
    [
      datesFilterProps,
      hoursFilterProps,
      lecturableFilterProps,
      nameFilterProps,
      priceFilterProps,
      t,
    ],
  );

  if (!courses) return <Spinner />;

  return (
    <>
      <DataGrid
        rowKey={(course) => course.id}
        columns={columns}
        dataSource={topRows}
        permissionsBasic={'fri-edu-courses'}
        onEditClick={onEditClick}
        uniqueTableId={'fri-edu-courses-table'}
        onRowClick={
          !isPublic
            ? (row) => {
                if (isUserAndItsMorphIdentical(user, row.lecturerable)) {
                  onRowClick?.(row);
                } else {
                  onDetailClick?.(row);
                }
              }
            : undefined
        }
        onRowDetailClick={onDetailClick}
        customSettings={(row) => (
          <EduCoursesTableSettings
            user={user}
            isAdmin={isAdmin}
            eduCourse={row}
            onShowDatesClick={onShowDatesClick}
            onDetailClick={onDetailClick}
            isPublic={isPublic}
          />
        )}
        optionsWidth={isPublic ? 120 : 170}
        allowClick={!isPublic}
        seriousDeleteQuestion={(row) =>
          t(
            'Naozaj chcete vymazať kurz "{{name}}"? Vymažú sa aj všetky jeho termíny a účastníci na nich. Operáciu nie je možné vrátiť späť!',
            { name: row?.name || '—' },
          )
        }
        disableExpandRow={(record) => !(record?.__terms?.length > 0)}
        customExpandedRowRender={(record) => (
          <EduCourseDatesNestedTable
            courseDetail={record}
            dates={record?.__terms || []}
            isAdmin={isAdmin}
            isAuthor={isUserAndItsMorphIdentical(user, record.lecturerable)}
            canUpdate={canUpdate}
            onRefetchCourses={onRefetchCourses}
            isPublic={isPublic}
          />
        )}
        secondaryCondition={(row) =>
          isUserAndItsMorphIdentical(user, row.lecturerable)
        }
        editDisabledTooltip={t('Upraviť môže len autor')}
        deleteCondition={(row) => {
          const isAuthor = isUserAndItsMorphIdentical(user, row.lecturerable);
          const hasEduAdminRole =
            Array.isArray(user?.roles) &&
            user.roles.some((r) => r.name === ROLE_FRI_EDU_ADMIN);
          return (isAuthor || hasEduAdminRole) && canDeleteCourse(row);
        }}
        deleteDisabledTooltip={(row) => {
          const isAuthor = isUserAndItsMorphIdentical(user, row.lecturerable);
          const hasEduAdminRole =
            Array.isArray(user?.roles) &&
            user.roles.some((r) => r.name === ROLE_FRI_EDU_ADMIN);
          if (!(isAuthor || hasEduAdminRole))
            return t('Kurz môže vymazať len autor alebo Administrátor FRI.edu');
          return t('Kurz už má termíny');
        }}
        {...(expandedRowKeys ? { expandedRowKeys } : {})}
        {...(onExpandedRowsChange ? { onExpandedRowsChange } : {})}
        onDelete={!isPublic ? onDelete : undefined}
      />
    </>
  );
};

EduCoursesTable.propTypes = {
  courses: PropTypes.array,
  onDelete: PropTypes.func,
  onEditClick: PropTypes.func,
  onDetailClick: PropTypes.func,
  onShowDatesClick: PropTypes.func,
  isPublic: PropTypes.bool,
  onRowClick: PropTypes.func,
  expandedRowKeys: PropTypes.array,
  onExpandedRowsChange: PropTypes.func,
  onRefetchCourses: PropTypes.func,
};

export default EduCoursesTable;
